import React, { useState, useEffect } from 'react';
import { Save, Palette, Mail, CheckCircle, AlertCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import ModernSettingHeader from '@/components/settings/ModernSettingHeader';
import { getClientBranding, updateClientBranding, verifyBrandingEmail, checkEmailVerificationStatus, ClientBranding } from '@/actions/drip-campaigns';

const SOCIAL_PLATFORMS = ['facebook', 'twitter', 'linkedin', 'instagram', 'youtube'];

const ClientBrandingPage: React.FC = () => {
  const [branding, setBranding] = useState<Partial<ClientBranding>>({
    logo_url: '',
    logo_width: 200,
    logo_height: 60,
    primary_color: '#0066cc',
    secondary_color: '#333333',
    accent_color: '#ff6600',
    background_color: '#f5f5f5',
    company_name: '',
    company_address: '',
    support_email: '',
    support_phone: '',
    website_url: '',
    social_links: {},
    footer_text: '',
    show_unsubscribe_link: true,
    unsubscribe_text: 'Unsubscribe from this list',
    show_address: true,
    show_social_links: true,
    custom_disclaimer: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<{
    verified: boolean;
    status: string;
    message: string;
  } | null>(null);

  useEffect(() => {
    loadBranding();
  }, []);

  useEffect(() => {
    if (branding.from_email && !loading) {
      checkVerificationStatus();
    }
  }, [branding.from_email, loading]);

  const loadBranding = async () => {
    try {
      setLoading(true);
      const response = await getClientBranding();
      if (response.success && response.data) {
        setBranding(response.data);
      }
    } catch (error: any) {
      // If no branding exists yet, that's okay - we'll create it on save
      if (error.response?.status !== 404) {
        console.error('Error loading branding:', error);
        toast.error('Failed to load branding settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ClientBranding, value: any) => {
    setBranding((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSocialLinkChange = (platform: string, url: string) => {
    setBranding((prev) => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [platform]: url,
      },
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateClientBranding(branding);
      toast.success('Branding settings saved successfully');
      setHasChanges(false);
      loadBranding();
    } catch (error: any) {
      console.error('Error saving branding:', error);
      toast.error(error.response?.data?.error || 'Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!branding.from_email) {
      toast.error('Please enter a from email address first');
      return;
    }

    // Save first if there are changes
    if (hasChanges) {
      try {
        await handleSave();
      } catch (error) {
        toast.error('Please save your changes first');
        return;
      }
    }

    try {
      setVerifying(true);
      const response = await verifyBrandingEmail();
      if (response.success) {
        toast.success(response.message);
        setTimeout(() => checkVerificationStatus(), 2000);
      }
    } catch (error: any) {
      console.error('Error verifying email:', error);
      toast.error(error.response?.data?.error || 'Failed to initiate email verification');
    } finally {
      setVerifying(false);
    }
  };

  const checkVerificationStatus = async () => {
    if (!branding.from_email) {
      setVerificationStatus(null);
      return;
    }

    try {
      setCheckingStatus(true);
      const response = await checkEmailVerificationStatus();
      if (response.success) {
        setVerificationStatus({
          verified: (response as any).verified || false,
          status: (response as any).status || 'Unknown',
          message: (response as any).message || 'Status unavailable',
        });
      }
    } catch (error: any) {
      console.error('Error checking verification status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading branding settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ModernSettingHeader
        title="Email Branding"
        description="Customize how your emails look with your brand colors, logo, and company information"
        icon={<Palette className="w-6 h-6" />}
      />

      <div className="p-6">
      {/* Save Button - Fixed at top */}
      {hasChanges && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            You have unsaved changes
          </p>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Logo Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Logo
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Logo URL
                </label>
                <input
                  type="url"
                  value={branding.logo_url || ''}
                  onChange={(e) => handleInputChange('logo_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com/logo.png"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Width (px)
                  </label>
                  <input
                    type="number"
                    value={branding.logo_width || 200}
                    onChange={(e) => handleInputChange('logo_width', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Height (px)
                  </label>
                  <input
                    type="number"
                    value={branding.logo_height || 60}
                    onChange={(e) => handleInputChange('logo_height', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              {branding.logo_url && (
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Preview:</p>
                  <img
                    src={branding.logo_url}
                    alt="Logo preview"
                    style={{
                      width: `${branding.logo_width}px`,
                      height: `${branding.logo_height}px`,
                    }}
                    className="object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Colors Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Brand Colors
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Primary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={branding.primary_color || '#0066cc'}
                    onChange={(e) => handleInputChange('primary_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={branding.primary_color || '#0066cc'}
                    onChange={(e) => handleInputChange('primary_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Secondary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={branding.secondary_color || '#333333'}
                    onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={branding.secondary_color || '#333333'}
                    onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Accent Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={branding.accent_color || '#ff6600'}
                    onChange={(e) => handleInputChange('accent_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={branding.accent_color || '#ff6600'}
                    onChange={(e) => handleInputChange('accent_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Background Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={branding.background_color || '#f5f5f5'}
                    onChange={(e) => handleInputChange('background_color', e.target.value)}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={branding.background_color || '#f5f5f5'}
                    onChange={(e) => handleInputChange('background_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Company Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={branding.company_name || ''}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Acme Inc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Address
                </label>
                <textarea
                  value={branding.company_address || ''}
                  onChange={(e) => handleInputChange('company_address', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="123 Main St, City, State 12345"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Support Email
                  </label>
                  <input
                    type="email"
                    value={branding.support_email || ''}
                    onChange={(e) => handleInputChange('support_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="support@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Support Phone
                  </label>
                  <input
                    type="tel"
                    value={branding.support_phone || ''}
                    onChange={(e) => handleInputChange('support_phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  value={branding.website_url || ''}
                  onChange={(e) => handleInputChange('website_url', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com"
                />
              </div>
            </div>
          </div>

          {/* From Address Configuration */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  From Address
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Configure the email address that drip campaigns will be sent from
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    From Email Address *
                  </label>
                  <input
                    type="email"
                    value={branding.from_email || ''}
                    onChange={(e) => handleInputChange('from_email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="noreply@yourcompany.com"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This email must be verified with AWS SES
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    From Name
                  </label>
                  <input
                    type="text"
                    value={branding.from_name || ''}
                    onChange={(e) => handleInputChange('from_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your Company"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Display name shown in emails
                  </p>
                </div>
              </div>

              {/* Verification Status */}
              {branding.from_email && (
                <div className="mt-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {verificationStatus?.verified ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium text-green-700 dark:text-green-400">
                            Email Verified
                          </span>
                        </>
                      ) : verificationStatus?.status === 'Pending' ? (
                        <>
                          <AlertCircle className="w-5 h-5 text-yellow-600" />
                          <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                            Verification Pending
                          </span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5 text-red-600" />
                          <span className="text-sm font-medium text-red-700 dark:text-red-400">
                            Not Verified
                          </span>
                        </>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={checkVerificationStatus}
                        disabled={checkingStatus}
                        className="flex items-center gap-1 px-3 py-1 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        <RefreshCw className={`w-4 h-4 ${checkingStatus ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                      {!verificationStatus?.verified && (
                        <button
                          onClick={handleVerifyEmail}
                          disabled={verifying || !branding.from_email}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          <Mail className="w-4 h-4" />
                          {verifying ? 'Sending...' : 'Verify Email'}
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                    {verificationStatus?.message || 'Click "Verify Email" to receive a verification link'}
                  </p>
                </div>
              )}

              {/* Information Box */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
                  How Email Verification Works
                </h4>
                <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1 list-disc list-inside">
                  <li>Enter your from email address and save settings</li>
                  <li>Click "Verify Email" to receive a verification email from AWS SES</li>
                  <li>Check your inbox and click the verification link</li>
                  <li>Come back and click "Refresh" to update the status</li>
                  <li>Once verified, all drip campaigns will use this email address</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Social Media Links
            </h2>
            <div className="space-y-4">
              {SOCIAL_PLATFORMS.map((platform) => (
                <div key={platform}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 capitalize">
                    {platform}
                  </label>
                  <input
                    type="url"
                    value={branding.social_links?.[platform] || ''}
                    onChange={(e) => handleSocialLinkChange(platform, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`https://${platform}.com/yourcompany`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Footer Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Email Footer
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Footer Text
                </label>
                <textarea
                  value={branding.footer_text || ''}
                  onChange={(e) => handleInputChange('footer_text', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Thank you for choosing our services..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Custom Disclaimer
                </label>
                <textarea
                  value={branding.custom_disclaimer || ''}
                  onChange={(e) => handleInputChange('custom_disclaimer', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Legal disclaimer or privacy notice..."
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={branding.show_address || false}
                    onChange={(e) => handleInputChange('show_address', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Show company address in footer
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={branding.show_social_links || false}
                    onChange={(e) => handleInputChange('show_social_links', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Show social media links in footer
                  </span>
                </label>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={branding.show_unsubscribe_link || false}
                    onChange={(e) => handleInputChange('show_unsubscribe_link', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Show unsubscribe link
                  </span>
                </label>
                {branding.show_unsubscribe_link && (
                  <input
                    type="text"
                    value={branding.unsubscribe_text || ''}
                    onChange={(e) => handleInputChange('unsubscribe_text', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    placeholder="Unsubscribe text"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Preview Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 sticky top-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Preview
            </h2>
            <div
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              style={{ backgroundColor: branding.background_color }}
            >
              {/* Logo */}
              {branding.logo_url && (
                <div className="mb-4 text-center">
                  <img
                    src={branding.logo_url}
                    alt="Logo"
                    style={{
                      width: `${(branding.logo_width || 200) / 2}px`,
                      height: `${(branding.logo_height || 60) / 2}px`,
                    }}
                    className="mx-auto object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Content Sample */}
              <div className="bg-white p-3 rounded mb-4">
                <h3
                  className="text-sm font-bold mb-2"
                  style={{ color: branding.primary_color }}
                >
                  Sample Heading
                </h3>
                <p className="text-xs mb-2" style={{ color: branding.secondary_color }}>
                  This is how your email content will look with your brand colors.
                </p>
                <button
                  className="text-xs px-3 py-1 rounded text-white"
                  style={{ backgroundColor: branding.accent_color }}
                >
                  Call to Action
                </button>
              </div>

              {/* Footer Sample */}
              <div className="text-center pt-3 border-t border-gray-200">
                {branding.footer_text && (
                  <p className="text-xs text-gray-600 mb-2">{branding.footer_text}</p>
                )}
                {branding.company_name && (
                  <p className="text-xs font-semibold text-gray-800 mb-1">
                    {branding.company_name}
                  </p>
                )}
                {branding.show_address && branding.company_address && (
                  <p className="text-xs text-gray-600 mb-2">{branding.company_address}</p>
                )}

                {/* Contact Information */}
                <div className="text-xs text-gray-600 mb-2 space-y-1">
                  {branding.support_email && (
                    <p>
                      Email: <a href={`mailto:${branding.support_email}`} className="text-blue-600 hover:underline">{branding.support_email}</a>
                    </p>
                  )}
                  {branding.support_phone && (
                    <p>
                      Phone: <a href={`tel:${branding.support_phone}`} className="text-blue-600 hover:underline">{branding.support_phone}</a>
                    </p>
                  )}
                  {branding.website_url && (
                    <p>
                      <a href={branding.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Visit our website
                      </a>
                    </p>
                  )}
                </div>

                {/* Social Media Links */}
                {branding.show_social_links && branding.social_links && Object.keys(branding.social_links).some(platform => branding.social_links?.[platform]) && (
                  <div className="flex justify-center gap-3 mb-2">
                    {SOCIAL_PLATFORMS.map((platform) => {
                      const url = branding.social_links?.[platform];
                      if (!url) return null;
                      return (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-600 hover:text-blue-600"
                          title={platform}
                        >
                          <div className="w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold uppercase">{platform[0]}</span>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}

                {/* Custom Disclaimer */}
                {branding.custom_disclaimer && (
                  <p className="text-xs text-gray-500 mb-2 italic">
                    {branding.custom_disclaimer}
                  </p>
                )}

                {branding.show_unsubscribe_link && (
                  <p className="text-xs">
                    <a href="#" className="text-blue-600 underline">
                      {branding.unsubscribe_text || 'Unsubscribe'}
                    </a>
                  </p>
                )}
              </div>
            </div>

            {/* Save Button - Inside Preview Sidebar */}
            <div className="mt-4">
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : 'Save Branding Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ClientBrandingPage;
